
import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Menu, Save, RefreshCw, Trash2, ArrowUp, ArrowDown, Plus, Edit2, X, CheckSquare, Square, Folder, GripVertical, ArrowUpDown } from 'lucide-react';
import { ICON_MAP } from '../components/Layout';

interface NavItemData {
    id: number;
    label: string;
    path: string;
    icon: string;
    category: string;
    allowed_roles: string[];
    sort_order: number;
    is_active: boolean;
}

const AVAILABLE_ROLES = ['WEBADMIN', 'ADMIN', 'STEWARD', 'DRIVER', 'CASTER', 'TELEMETRY'];

export const NavigationAdmin: React.FC = () => {
    const { user } = useAuth();
    const [items, setItems] = useState<NavItemData[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<Partial<NavItemData> | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    
    // Drag & Drop State
    const [draggedItem, setDraggedItem] = useState<NavItemData | null>(null);
    const [dropTargetCategory, setDropTargetCategory] = useState<string | null>(null);

    // Only WebAdmins
    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const loadItems = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<NavItemData[]>('navigation.php');
            setItems(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadItems(); }, []);

    // --- LOGIC: GROUPING & SORTING ---

    // 1. Group items by category
    const groupedItems = items.reduce((acc, item) => {
        const cat = item.category || 'Unkategorisiert';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {} as Record<string, NavItemData[]>);

    // 2. Determine category order based on the lowest sort_order of items in that category
    const categories = Object.keys(groupedItems).sort((a, b) => {
        const minA = Math.min(...groupedItems[a].map(i => i.sort_order));
        const minB = Math.min(...groupedItems[b].map(i => i.sort_order));
        return minA - minB;
    });

    // Save the new order to DB
    const persistOrder = async (newFlatList: NavItemData[]) => {
        setItems(newFlatList); // Optimistic UI update
        try {
            await apiRequest('navigation.php', 'POST', {
                action: 'reorder',
                items: newFlatList.map(i => i.id)
            });
        } catch(e:any) { 
            alert(e.message); 
            loadItems(); // Revert on error
        }
    };

    // --- DRAG & DROP LOGIC ---

    const handleDragStart = (e: React.DragEvent, item: NavItemData) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = "move";
        // Ghost image usually handled by browser automatically
    };

    const handleDragOver = (e: React.DragEvent, category: string) => {
        e.preventDefault(); // Necessary to allow dropping
        if (dropTargetCategory !== category) {
            setDropTargetCategory(category);
        }
    };

    const handleDrop = async (e: React.DragEvent, targetCategory: string) => {
        e.preventDefault();
        setDropTargetCategory(null);
        
        if (!draggedItem || draggedItem.category === targetCategory) return;

        // Optimistic Update
        const updatedItem = { ...draggedItem, category: targetCategory };
        const newItems = items.map(i => i.id === draggedItem.id ? updatedItem : i);
        setItems(newItems);
        setDraggedItem(null);

        // API Call to update just this item
        try {
            await apiRequest('navigation.php', 'PUT', updatedItem);
        } catch(e: any) {
            alert("Fehler beim Verschieben: " + e.message);
            loadItems();
        }
    };

    // --- SORTING ---

    const handleMoveItem = (itemId: number, direction: 'up' | 'down') => {
        const currentItem = items.find(i => i.id === itemId);
        if (!currentItem) return;
        
        const catItems = groupedItems[currentItem.category || 'Unkategorisiert'];
        const indexInCat = catItems.findIndex(i => i.id === itemId);
        
        if (direction === 'up' && indexInCat === 0) return;
        if (direction === 'down' && indexInCat === catItems.length - 1) return;

        const targetIndex = direction === 'up' ? indexInCat - 1 : indexInCat + 1;
        
        const newCatItems = [...catItems];
        [newCatItems[indexInCat], newCatItems[targetIndex]] = [newCatItems[targetIndex], newCatItems[indexInCat]];
        
        const newFlatList: NavItemData[] = [];
        categories.forEach(cat => {
            if (cat === currentItem.category) {
                newFlatList.push(...newCatItems);
            } else {
                newFlatList.push(...groupedItems[cat]);
            }
        });

        persistOrder(newFlatList);
    };

    const handleMoveCategory = (category: string, direction: 'up' | 'down') => {
        const catIndex = categories.indexOf(category);
        if (direction === 'up' && catIndex === 0) return;
        if (direction === 'down' && catIndex === categories.length - 1) return;

        const targetIndex = direction === 'up' ? catIndex - 1 : catIndex + 1;
        
        const newCategories = [...categories];
        [newCategories[catIndex], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[catIndex]];

        const newFlatList: NavItemData[] = [];
        newCategories.forEach(cat => {
            newFlatList.push(...groupedItems[cat]);
        });

        persistOrder(newFlatList);
    };

    // --- CRUD & RENAME ---

    const handleRenameCategory = async (oldName: string) => {
        const newName = prompt(`Kategorie "${oldName}" umbenennen in:`, oldName);
        if (!newName || newName === oldName) return;

        try {
            await apiRequest('navigation.php', 'POST', {
                action: 'rename_category',
                oldName,
                newName
            });
            loadItems();
        } catch(e: any) {
            alert(e.message);
        }
    };

    const handleSave = async () => {
        if (!editingItem || !editingItem.label || !editingItem.path) return;
        try {
            if (isCreating) {
                await apiRequest('navigation.php', 'POST', editingItem);
            } else {
                await apiRequest('navigation.php', 'PUT', editingItem);
            }
            setEditingItem(null);
            setIsCreating(false);
            loadItems();
        } catch(e:any) { alert(e.message); }
    };

    const handleDelete = async (id: number) => {
        if(!confirm("Menüpunkt löschen?")) return;
        try {
            await apiRequest(`navigation.php?id=${id}`, 'DELETE');
            loadItems();
        } catch(e:any) { alert(e.message); }
    };

    const toggleRole = (role: string) => {
        if (!editingItem) return;
        const roles = editingItem.allowed_roles || [];
        if (roles.includes(role)) {
            setEditingItem({...editingItem, allowed_roles: roles.filter(r => r !== role)});
        } else {
            setEditingItem({...editingItem, allowed_roles: [...roles, role]});
        }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                    <Menu size={32}/> Navigation verwalten
                    </h1>
                    <p className="text-gray-400 text-sm">Menüstruktur & Zugriffsrechte verwalten</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" icon={RefreshCw} onClick={loadItems}>Refresh</Button>
                    <Button icon={Plus} onClick={() => { 
                        setEditingItem({ label: '', path: '', icon: 'LayoutDashboard', category: 'Allgemein', allowed_roles: [], is_active: true }); 
                        setIsCreating(true); 
                    }}>Neu</Button>
                </div>
            </div>

            {/* HINT FOR DRAG AND DROP */}
            <div className="bg-blue-900/20 border border-blue-800 p-3 rounded text-sm text-blue-200 flex items-center gap-3">
                <ArrowUpDown size={18}/>
                <span>Tipp: Ziehe Menüpunkte mit der Maus in andere Kategorien, um sie zu verschieben.</span>
            </div>

            {/* CATEGORY BLOCKS */}
            <div className="space-y-6">
                {categories.map((cat, catIdx) => {
                    const catItems = groupedItems[cat];
                    const isDropTarget = dropTargetCategory === cat;
                    
                    return (
                        <div 
                            key={cat} 
                            className={`bg-f1-carbon border rounded-xl overflow-hidden shadow-lg transition-all duration-200 ${isDropTarget ? 'border-f1-red ring-2 ring-f1-red/50 scale-[1.01]' : 'border-gray-700'}`}
                            onDragOver={(e) => handleDragOver(e, cat)}
                            onDrop={(e) => handleDrop(e, cat)}
                        >
                            {/* Category Header */}
                            <div className="bg-gray-800 p-3 border-b border-gray-700 flex justify-between items-center group/cat">
                                <div className="flex items-center gap-3">
                                    <div className="bg-gray-700 p-1.5 rounded text-gray-300">
                                        <Folder size={16}/>
                                    </div>
                                    <h3 className="font-bold text-white text-lg uppercase">{cat}</h3>
                                    
                                    {/* RENAME BUTTON */}
                                    <button 
                                        onClick={() => handleRenameCategory(cat)}
                                        className="p-1.5 hover:bg-gray-700 rounded text-gray-500 hover:text-white opacity-0 group-hover/cat:opacity-100 transition-opacity"
                                        title="Kategorie umbenennen"
                                    >
                                        <Edit2 size={12}/>
                                    </button>

                                    <span className="text-xs text-gray-500 font-mono">({catItems.length} Items)</span>
                                </div>
                                <div className="flex gap-1">
                                    <button 
                                        onClick={() => handleMoveCategory(cat, 'up')} 
                                        disabled={catIdx === 0}
                                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 disabled:opacity-30"
                                        title="Kategorie hoch"
                                    >
                                        <ArrowUp size={16}/>
                                    </button>
                                    <button 
                                        onClick={() => handleMoveCategory(cat, 'down')} 
                                        disabled={catIdx === categories.length - 1}
                                        className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 disabled:opacity-30"
                                        title="Kategorie runter"
                                    >
                                        <ArrowDown size={16}/>
                                    </button>
                                </div>
                            </div>

                            {/* Items List */}
                            <div className="divide-y divide-gray-800">
                                {catItems.map((item, itemIdx) => {
                                    const ItemIcon = ICON_MAP[item.icon] || Menu;
                                    return (
                                        <div 
                                            key={item.id} 
                                            className={`flex items-center p-3 hover:bg-gray-800/50 transition-colors cursor-grab active:cursor-grabbing ${!item.is_active ? 'opacity-50' : ''}`}
                                            draggable={true}
                                            onDragStart={(e) => handleDragStart(e, item)}
                                        >
                                            {/* Drag Handle Visual */}
                                            <div className="mr-3 text-gray-600">
                                                <GripVertical size={16}/>
                                            </div>
                                            
                                            {/* Icon */}
                                            <div className="w-10 flex justify-center text-gray-400">
                                                <ItemIcon size={20}/>
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0 mr-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-white text-sm">{item.label}</span>
                                                    {!item.is_active && <span className="text-[9px] bg-red-900/30 text-red-500 px-1 rounded border border-red-900/50">INAKTIV</span>}
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono truncate">{item.path}</div>
                                            </div>

                                            {/* Roles */}
                                            <div className="hidden md:flex gap-1 mr-4">
                                                {item.allowed_roles.length === 0 ? (
                                                    <Badge className="bg-green-900/20 text-green-500 border-green-900/30 text-[9px]">PUBLIC</Badge>
                                                ) : (
                                                    item.allowed_roles.map(r => (
                                                        <span key={r} className="text-[9px] px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded text-gray-400 font-bold">{r}</span>
                                                    ))
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2">
                                                <div className="flex flex-col gap-0.5 mr-2">
                                                    <button onClick={() => handleMoveItem(item.id, 'up')} disabled={itemIdx === 0} className="text-gray-500 hover:text-white disabled:opacity-20"><ArrowUp size={12}/></button>
                                                    <button onClick={() => handleMoveItem(item.id, 'down')} disabled={itemIdx === catItems.length - 1} className="text-gray-500 hover:text-white disabled:opacity-20"><ArrowDown size={12}/></button>
                                                </div>
                                                
                                                <button onClick={() => setEditingItem(item)} className="p-2 bg-blue-900/20 text-blue-400 rounded hover:bg-blue-900/40"><Edit2 size={16}/></button>
                                                <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-900/20 text-red-400 rounded hover:bg-red-900/40"><Trash2 size={16}/></button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Empty State / Add to Cat Button could go here */}
                            <div className="p-2 bg-gray-800/30 border-t border-gray-800 text-center">
                                <button 
                                    onClick={() => {
                                        setEditingItem({ label: '', path: '', icon: 'Circle', category: cat, allowed_roles: [], is_active: true }); 
                                        setIsCreating(true);
                                    }}
                                    className="text-xs text-gray-500 hover:text-white flex items-center justify-center gap-1 w-full py-1"
                                >
                                    <Plus size={12}/> Eintrag in "{cat}" hinzufügen
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* EDIT MODAL */}
            {editingItem && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-600">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold uppercase text-white">{isCreating ? 'Neuer Eintrag' : 'Bearbeiten'}</h3>
                            <button onClick={() => { setEditingItem(null); setIsCreating(false); }}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <Input label="Label" value={editingItem.label} onChange={e => setEditingItem({...editingItem, label: e.target.value})} placeholder="Menüname"/>
                                <Input label="Pfad (Route)" value={editingItem.path} onChange={e => setEditingItem({...editingItem, path: e.target.value})} placeholder="/mein-pfad"/>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Icon (Lucide)</label>
                                    <select 
                                        className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                        value={editingItem.icon}
                                        onChange={e => setEditingItem({...editingItem, icon: e.target.value})}
                                    >
                                        {Object.keys(ICON_MAP).sort().map(k => <option key={k} value={k}>{k}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Kategorie</label>
                                    <input 
                                        className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white list-none"
                                        list="cat-list"
                                        value={editingItem.category}
                                        onChange={e => setEditingItem({...editingItem, category: e.target.value})}
                                        placeholder="Neue oder wählen..."
                                    />
                                    <datalist id="cat-list">
                                        {categories.map(c => <option key={c} value={c}/>)}
                                    </datalist>
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Sichtbar für Rollen (Leer = Öffentlich)</label>
                                <div className="grid grid-cols-3 gap-2 bg-gray-900 p-3 rounded border border-gray-700">
                                    {AVAILABLE_ROLES.map(role => {
                                        const isChecked = editingItem.allowed_roles?.includes(role);
                                        return (
                                            <div key={role} onClick={() => toggleRole(role)} className={`cursor-pointer flex items-center gap-2 p-1.5 rounded transition-colors ${isChecked ? 'bg-green-900/30 text-green-400 border border-green-800' : 'text-gray-500 hover:bg-gray-800'}`}>
                                                {isChecked ? <CheckSquare size={14}/> : <Square size={14}/>}
                                                <span className="text-xs font-bold">{role}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-2 border-t border-gray-700">
                                <input type="checkbox" checked={editingItem.is_active} onChange={e => setEditingItem({...editingItem, is_active: e.target.checked})} className="w-4 h-4 rounded bg-gray-900 border-gray-600 text-f1-red"/>
                                <span className="text-sm font-bold text-white">Menüpunkt Aktiv</span>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="ghost" onClick={() => { setEditingItem(null); setIsCreating(false); }}>Abbrechen</Button>
                            <Button onClick={handleSave} icon={Save}>Speichern</Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
